"use client"

import { useAutoAnimate } from "@formkit/auto-animate/react"
import { useState } from "react"
import { extractAndRecordCouponErrorMessage } from "src/checkout/hooks/extractCouponErrorMessage"
import { addCouponOrThrow, useCartState } from "src/checkout/hooks/useCart"
import { useCoupons } from "src/checkout/hooks/useCoupon"
import { NpButton } from "src/components/np-design/components/button/NpButton"
import { NpErrorMessage } from "src/components/np-design/components/NpErrorMessage"
import { NpIcon } from "src/components/np-design/components/NpIcon"
import { NpSpinner } from "src/components/np-design/components/NpSpinner"
import { useEventCallback } from "src/hooks/useEventCallback"

export type NpActivateCouponRegionProps = {
  code: string
  amountSaved: string
}

export const NpActivateCouponRegion = ({ code, amountSaved }: NpActivateCouponRegionProps) => {
  const coupons = useCoupons()
  const { loading } = useCartState()
  const [adding, setAdding] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [animationParent] = useAutoAnimate()

  const isActive = coupons.some((coupon) => coupon.code.toUpperCase() === code.toUpperCase())

  const tryAddCoupon = useEventCallback(async () => {
    if (!code || isActive) {
      setErrorMessage("")
      return
    }

    setAdding(true)
    setErrorMessage("")

    try {
      await addCouponOrThrow(code)
    } catch (error) {
      const message = extractAndRecordCouponErrorMessage(code, error)
      setErrorMessage(message)
    } finally {
      setAdding(false)
    }
  })

  return (
    <section
      aria-label="Rabattcode"
      ref={animationParent}
      className="border-np-green25 flex max-w-100 flex-col items-center justify-start gap-4 rounded-2xl border p-6 text-center whitespace-nowrap"
    >
      <div className="flex flex-col items-center gap-1 whitespace-normal">
        <p className="font-medium">Rabattcode heute noch gültig</p>
        <p className="leading-normal font-light">
          Spare heute {amountSaved} mit diesem Rabattcode auf ausgewählte Modelle.
        </p>
      </div>

      <div className="text-np-green font-medium uppercase">{code}</div>

      {isActive ? (
        <div className="text-np-green flex flex-col items-center text-center">
          <NpIcon name="check_circle" className="size-7" />
          <div className="font-medium">Code aktiv</div>
        </div>
      ) : loading ? (
        <NpSpinner />
      ) : errorMessage ? (
        <>
          <NpErrorMessage message={errorMessage} />
          <NpButton variant="outlined" size="sm" onClick={tryAddCoupon} loading={adding}>
            Erneut versuchen
          </NpButton>
        </>
      ) : (
        <NpButton variant="outlined" size="sm" onClick={tryAddCoupon} loading={adding}>
          Code aktivieren
        </NpButton>
      )}
    </section>
  )
}
